import {Injectable, OnDestroy} from '@angular/core';
import {MatSnackBar, MatSnackBarRef} from "@angular/material/snack-bar";
import {AuthService} from "./auth.service";
import {take} from "rxjs";
import {TimeoutStateService} from "./timeout-state.service";

@Injectable({
    providedIn: 'root'
})
export class TimeoutNotifierService implements OnDestroy {
    // Browsers overflow delays longer than a signed 32-bit integer (about 25 days).
    private readonly maxTimerDelay = 2_147_483_647;
    private readonly storageListener = (event: StorageEvent): void => this.handleTimeoutStorageEvent(event);
    private warningTimeout?: ReturnType<typeof setTimeout>;
    private logoutTimeout?: ReturnType<typeof setTimeout>;
    private timeoutSnackBarRef?: MatSnackBarRef<any>;
    private activeTimeoutExpiresAt?: number;

    constructor(private snackBar: MatSnackBar,
                private timeoutStateService: TimeoutStateService,
                private authService: AuthService) {
        window.addEventListener('storage', this.storageListener);
        this.authService.storeCleared$.subscribe((): void => this.clearTimeoutState());
    }

    timeoutNotifier(totalTimeout: number): void {
        const expiresAt: number = this.timeoutStateService.setSharedTimeout(totalTimeout);
        this.scheduleTimeoutTimers(expiresAt);
    }

    isLoggedIn(): boolean {
        return localStorage.getItem('access_token') != null;
    }

    private scheduleTimeoutTimers(expiresAt: number): void {
        const warningDuration: number = 60000; // 1 minute
        const timeRemaining: number = expiresAt - Date.now();
        const warningDelay: number = timeRemaining - warningDuration;

        this.activeTimeoutExpiresAt = expiresAt;
        clearTimeout(this.warningTimeout);
        clearTimeout(this.logoutTimeout);
        this.timeoutSnackBarRef?.dismiss();

        // Show warning before expiry
        this.warningTimeout = setTimeout((): void => {
            if (!this.isLoggedIn() || !this.timeoutStateService.isActiveTimeout(expiresAt)) {
                return;
            }
            if (expiresAt - Date.now() > warningDuration) {
                this.scheduleTimeoutTimers(expiresAt);
                return;
            }

            this.timeoutSnackBarRef = this.snackBar.open(
                'You will soon be timed out due to inactivity; please click to continue',
                'Continue',
                {
                    duration: warningDuration,
                    panelClass: ['warn'],
                }
            );
            this.timeoutSnackBarRef.onAction().pipe(take(1)).subscribe(async (): Promise<void> => {
                // User continued session
                try {
                    await this.authService.refreshUser();
                    this.extendSharedTimeoutFromStoredDuration();
                    this.timeoutSnackBarRef?.dismiss();
                } catch {
                    this.timeoutSnackBarRef?.dismiss();
                    this.snackBar.open('Unable to extend your session. Check your connection and try again.', 'Dismiss');
                }
            });

        }, Math.min(Math.max(warningDelay, 0), this.maxTimerDelay));

        // Force logout at actual timeout
        this.logoutTimeout = setTimeout((): void => {
            if (!this.isLoggedIn() || !this.timeoutStateService.isActiveTimeout(expiresAt)) {
                return;
            }
            if (Date.now() < expiresAt) {
                this.scheduleTimeoutTimers(expiresAt);
                return;
            }

            this.timeoutSnackBarRef?.dismiss();
            this.snackBar.open(
                'For security reasons you have been logged out due to inactivity please login again for access',
                'Continue',
                {
                    duration: 60000,
                    panelClass: ['error'],
                }
            );

            this.authService.logout(window.location.pathname + window.location.search);
        }, Math.min(Math.max(timeRemaining, 0), this.maxTimerDelay));
    }

    private handleTimeoutStorageEvent(event: StorageEvent): void {
        if (event.key !== this.timeoutStateService.timeoutExpiresAtStorageKey) {
            return;
        }

        if (!event.newValue) {
            this.clearTimeoutTimers();
            return;
        }

        const expiresAt: number = Number(event.newValue);
        if (!Number.isFinite(expiresAt) || expiresAt === this.activeTimeoutExpiresAt) {
            return;
        }

        this.scheduleTimeoutTimers(expiresAt);
    }

    private extendSharedTimeoutFromStoredDuration(): void {
        const duration: number | null = this.timeoutStateService.getStoredTimeoutDuration();
        if (!duration) {
            return;
        }

        const expiresAt: number = this.timeoutStateService.setSharedTimeout(duration);
        this.scheduleTimeoutTimers(expiresAt);
    }

    private clearTimeoutState(): void {
        this.clearTimeoutTimers();
        this.timeoutStateService.clearTimeoutState();
    }

    private clearTimeoutTimers(): void {
        clearTimeout(this.warningTimeout);
        clearTimeout(this.logoutTimeout);
        this.timeoutSnackBarRef?.dismiss();
        this.activeTimeoutExpiresAt = undefined;
    }

    ngOnDestroy(): void {
        window.removeEventListener('storage', this.storageListener);
        this.clearTimeoutTimers();
    }
}
